import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import { KeyRound, Plus, ShieldCheck, Trash2, TriangleAlert, Usb } from 'lucide-react';
import { useConfirm } from '@/components/common/ConfirmDialog';
import {
  passkeyAnlegen,
  passkeyLoeschen,
  passkeysLaden,
  passkeysMoeglich,
  type PasskeyInfo,
} from '@/api/passkeys.api';
import { useAdminAuth } from '@/store/useAdminAuth';

// ─── Mein Zugang ─────────────────────────────────────────────────────────────
//
// Passkeys für die Konsole. Sicherheitsschlüssel wie ein YubiKey zählen dazu:
// für den Browser ist beides dasselbe, nur einmal im Gerät und einmal am USB-
// Anschluss.
//
// Der Schlüssel gilt für BEIDE Oberflächen – Anwendung und Konsole –, weil er
// am Konto hängt und nicht an der Adresse. Wer sich in der Anwendung einen
// angelegt hat, findet ihn hier wieder. Das ist der Grund, warum diese Seite
// bewusst dieselbe Liste zeigt und nicht eine eigene führt.

function geraeteBeschriftung(p: PasskeyInfo): string {
  if (p.deviceName) return p.deviceName;
  const t = (p.transports ?? '').split(',');
  if (t.includes('usb')) return 'Sicherheitsschlüssel am USB-Anschluss';
  if (t.includes('nfc')) return 'Sicherheitsschlüssel über NFC';
  if (t.includes('internal')) return 'Dieses Gerät';
  return 'Unbenannter Schlüssel';
}

export function ZugangPage() {
  const queryClient = useQueryClient();
  const bestaetigen = useConfirm();
  const benutzer = useAdminAuth((s) => s.user);
  const [name, setName] = useState('');

  const moeglich = passkeysMoeglich();

  const { data: schluessel, isLoading } = useQuery({
    queryKey: ['admin', 'passkeys'],
    queryFn: passkeysLaden,
  });

  const anlegen = useMutation({
    mutationFn: () => passkeyAnlegen(name.trim() || 'Sicherheitsschlüssel'),
    onSuccess: () => {
      setName('');
      queryClient.invalidateQueries({ queryKey: ['admin', 'passkeys'] });
      toast.success('Schlüssel hinterlegt.');
    },
    onError: (e: Error) => {
      // Der Browser bricht auch beim Abbrechen durch den Nutzer mit einem
      // Fehler ab. Das ist kein Problem und soll nicht wie eines aussehen.
      if (e.name === 'NotAllowedError' || /abort|not allowed/i.test(e.message)) {
        toast('Abgebrochen.', { icon: '↩︎' });
        return;
      }
      toast.error(e.message || 'Der Schlüssel konnte nicht hinterlegt werden.');
    },
  });

  const loeschen = useMutation({
    mutationFn: (id: string) => passkeyLoeschen(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin', 'passkeys'] });
      toast.success('Schlüssel entfernt.');
    },
    onError: (e: Error) => toast.error(e.message),
  });

  const anzahl = schluessel?.length ?? 0;

  const entfernen = async (p: PasskeyInfo) => {
    const ok = await bestaetigen({
      title: 'Schlüssel entfernen?',
      message:
        anzahl === 1
          ? 'Das ist Ihr letzter Schlüssel. Danach schützt nur noch das Passwort ' +
            'diesen Zugang – und über ihn ist jede Kundenfamilie erreichbar.'
          : `„${geraeteBeschriftung(p)}" wird entfernt. Mit diesem Gerät ist danach ` +
            'keine Anmeldung mehr möglich.',
      confirmLabel: 'Entfernen',
      variant: 'danger',
    });
    if (ok) loeschen.mutate(p.id);
  };

  return (
    <div className="mx-auto max-w-3xl space-y-6 p-4 sm:p-6">
      <header>
        <h1 className="flex items-center gap-2 text-xl font-semibold text-slate-900 sm:text-2xl">
          <ShieldCheck className="h-5 w-5 text-slate-400" aria-hidden="true" />
          Mein Zugang
        </h1>
        <p className="mt-0.5 text-sm text-slate-500">
          Angemeldet als {benutzer?.email}. Was hier steht, gilt für Ihr Konto –
          in der Konsole wie in der Anwendung.
        </p>
      </header>

      <section className="rounded-xl border border-slate-200 bg-white p-5">
        <h2 className="flex items-center gap-2 text-sm font-semibold text-slate-900">
          <KeyRound className="h-4 w-4 text-slate-400" aria-hidden="true" />
          Passkeys und Sicherheitsschlüssel
        </h2>
        <p className="mt-1 text-sm leading-relaxed text-slate-500">
          Ein Passkey ersetzt beim Anmelden das Passwort oder tritt als zweiter
          Faktor daneben. Ein Sicherheitsschlüssel am USB-Anschluss – etwa ein
          YubiKey – ist dasselbe in Hardware. Beides ist gegen Phishing
          unempfindlich: der Schlüssel antwortet nur der Domain, für die er
          angelegt wurde.
        </p>

        {!moeglich && (
          <p className="mt-4 flex items-start gap-2 rounded-lg bg-amber-50 p-3 text-sm text-amber-900 ring-1 ring-amber-200">
            <TriangleAlert className="mt-0.5 h-4 w-4 shrink-0" aria-hidden="true" />
            Dieser Browser kann keine Passkeys. Über eine unverschlüsselte
            Verbindung geht es grundsätzlich nicht – außer auf localhost.
          </p>
        )}

        {/* ── Vorhandene Schlüssel ──────────────────────────────────────── */}
        <div className="mt-5">
          {isLoading ? (
            <p className="text-sm text-slate-500">Wird geladen…</p>
          ) : anzahl === 0 ? (
            <p className="rounded-lg border border-dashed border-slate-300 p-4 text-sm text-slate-500">
              Noch kein Schlüssel hinterlegt. Solange nur das Passwort schützt,
              genügt ein abgefangenes Passwort für den Zugang zu jeder
              Kundenfamilie.
            </p>
          ) : (
            <ul className="divide-y divide-slate-100 rounded-lg border border-slate-200">
              {schluessel!.map((p) => (
                <li key={p.id} className="flex items-center gap-3 px-4 py-3">
                  <Usb className="h-4 w-4 shrink-0 text-slate-400" aria-hidden="true" />
                  <div className="min-w-0 flex-1">
                    <p className="truncate text-sm font-medium text-slate-900">
                      {geraeteBeschriftung(p)}
                    </p>
                    <p className="text-xs text-slate-500">
                      hinterlegt am {new Date(p.createdAt).toLocaleDateString('de-DE')}
                    </p>
                  </div>
                  <button
                    onClick={() => entfernen(p)}
                    aria-label={`${geraeteBeschriftung(p)} entfernen`}
                    className="flex h-9 w-9 items-center justify-center rounded-lg text-slate-400 hover:bg-red-50 hover:text-red-600"
                  >
                    <Trash2 className="h-4 w-4" aria-hidden="true" />
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>

        {/* ── Neuen anlegen ─────────────────────────────────────────────── */}
        <div className="mt-5 flex flex-col gap-2 sm:flex-row">
          <input
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="Bezeichnung, z. B. YubiKey am Schlüsselbund"
            disabled={!moeglich || anlegen.isPending}
            className="w-full rounded-md border border-slate-300 px-3 py-2 text-sm focus:border-blue-400 focus:outline-none focus:ring-2 focus:ring-blue-100 disabled:bg-slate-50"
          />
          <button
            onClick={() => anlegen.mutate()}
            disabled={!moeglich || anlegen.isPending}
            className="inline-flex shrink-0 items-center justify-center gap-2 rounded-md bg-blue-600 px-4 py-2 text-sm font-medium text-white hover:bg-blue-700 disabled:opacity-50"
          >
            <Plus className="h-4 w-4" aria-hidden="true" />
            {anlegen.isPending ? 'Warte auf den Schlüssel…' : 'Schlüssel hinterlegen'}
          </button>
        </div>
        <p className="mt-2 text-xs text-slate-400">
          Die Bezeichnung dient nur der Wiedererkennung, wenn Sie mehrere
          Schlüssel haben. Zwei sind eine gute Zahl: einer am Schlüsselbund,
          einer im Schreibtisch. Geht der erste verloren, sperrt der zweite Sie
          nicht aus.
        </p>
      </section>

    </div>
  );
}

export default ZugangPage;
